/**
 * Render an emoji character as a Twemoji SVG image so it looks the same
 * on every OS (Windows in particular doesn't render flag emoji as flags
 * out of the box). Uses the public jsdelivr Twemoji mirror — no JS
 * dependency, just an inline codepoint converter.
 *
 * For most emoji we strip the FE0F variation selector. Keycap sequences
 * (1️⃣ etc.) keep theirs because FE0F + 20E3 is meaningful. Good enough
 * for campaign icons (flags, smileys, objects).
 */
const VS16 = 0xfe0f;
const KEYCAP = 0x20e3;

const toCodePoints = (emoji: string): string[] => {
  const out: string[] = [];
  // Iterate by code point (handles surrogate pairs).
  for (const ch of emoji) {
    out.push(ch.codePointAt(0)!.toString(16));
  }
  // Strip VS16 unless followed by a combining keycap.
  const filtered: string[] = [];
  for (let i = 0; i < out.length; i++) {
    const cp = parseInt(out[i], 16);
    if (cp === VS16) {
      const next = parseInt(out[i + 1] || '0', 16);
      if (next === KEYCAP) {
        filtered.push(out[i]); // keep FE0F before KEYCAP
        continue;
      }
      continue; // drop standalone VS16
    }
    filtered.push(out[i]);
  }
  return filtered;
};

const twemojiUrl = (emoji: string): string => {
  const code = toCodePoints(emoji).join('-');
  return `https://cdn.jsdelivr.net/gh/jdecked/twemoji@latest/assets/svg/${code}.svg`;
};

interface Props {
  emoji: string;
  size?: number;
  /** Alt-text override. Defaults to the emoji character itself. */
  alt?: string;
}

export const Twemoji = ({ emoji, size = 18, alt }: Props) => {
  if (!emoji) return null;
  return (
    <img
      src={twemojiUrl(emoji)}
      alt={alt ?? emoji}
      style={{
        height: size,
        width: size,
        verticalAlign: '-0.15em',
        display: 'inline-block',
      }}
      // Fall back to the raw character if the CDN URL fails (e.g. some
      // exotic emoji not in the SVG set).
      onError={(e) => {
        const span = document.createElement('span');
        span.textContent = emoji;
        span.style.fontSize = `${size}px`;
        e.currentTarget.replaceWith(span);
      }}
    />
  );
};

export default Twemoji;
